import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { getCurrencySymbol } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';

const IncomeModal = ({ open, onClose, income, onSuccess }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    project_id: '',
    amount: 0,
    expected_date: '',
    payment_status: 'pending',
    notes: '',
    account_id: '',
    hsn_sac: '',
    gst_rate: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    place_of_supply: '',
    is_reverse_charge: false
  });
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTax, setShowTax] = useState(false);

  useEffect(() => {
    if (open) {
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    if (income) {
      setFormData({
        project_id: income.project_id || '',
        amount: income.amount || 0,
        expected_date: income.expected_date || '',
        payment_status: income.payment_status || 'pending',
        notes: income.notes || '',
        account_id: income.account_id || '',
        hsn_sac: income.hsn_sac || '',
        gst_rate: income.gst_rate || 0,
        cgst: income.cgst || 0,
        sgst: income.sgst || 0,
        igst: income.igst || 0,
        place_of_supply: income.place_of_supply || '',
        is_reverse_charge: income.is_reverse_charge || false
      });
      if (income.gst_rate > 0) setShowTax(true);
    } else {
      const defaultAcc = accounts.find(a => a.is_default);
      setFormData({
        project_id: '',
        amount: 0,
        expected_date: '',
        payment_status: 'pending',
        notes: '',
        account_id: defaultAcc ? defaultAcc.account_id : (accounts[0]?.account_id || ''),
        hsn_sac: '',
        gst_rate: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        place_of_supply: '',
        is_reverse_charge: false
      });
    }
  }, [income, accounts, open]);

  const fetchData = async () => {
    try {
      const [projRes, accRes] = await Promise.all([
        apiClient.get('/projects'),
        apiClient.get('/accounts')
      ]);
      setProjects(projRes.data);
      setAccounts(accRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const validateForm = () => {
    if (!formData.project_id) {
      toast.error('Please select a project');
      return false;
    }
    if (formData.amount <= 0) {
      toast.error('Amount must be greater than 0');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFloat(formData.amount),
        gst_rate: parseFloat(formData.gst_rate || 0),
        cgst: parseFloat(formData.cgst || 0),
        sgst: parseFloat(formData.sgst || 0),
        igst: parseFloat(formData.igst || 0)
      };
      
      if (income) {
        await apiClient.put(`/income/${income.income_id}`, submitData);
        toast.success('Income record updated successfully');
      } else {
        await apiClient.post('/income', submitData);
        toast.success('Income record created successfully');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save income record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]" data-testid="income-modal">
        <DialogHeader>
          <DialogTitle>{income ? 'Edit Income Record' : 'Add Income Record'}</DialogTitle>
          <DialogDescription>
            {income ? 'Update income details' : 'Track a new payment'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
          <div className="space-y-2">
            <Label htmlFor="project">Project *</Label>
            <Select value={formData.project_id} onValueChange={(value) => setFormData({ ...formData, project_id: value })}>
              <SelectTrigger className="bg-slate-50" data-testid="income-project-select">
                <SelectValue placeholder="Select a project" />
              </SelectTrigger>
              <SelectContent>
                {projects.map((project) => (
                  <SelectItem key={project.project_id} value={project.project_id}>
                    {project.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="amount">Amount ({getCurrencySymbol(user?.currency)}) *</Label>
            <Input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
              placeholder="50000"
              className="bg-slate-50"
              data-testid="income-amount-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expected_date">Expected Date</Label>
            <Input
              id="expected_date"
              type="date"
              value={formData.expected_date}
              onChange={(e) => setFormData({ ...formData, expected_date: e.target.value })}
              className="bg-slate-50"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.payment_status} onValueChange={(value) => setFormData({ ...formData, payment_status: value })}>
                <SelectTrigger className="bg-slate-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="received">Received</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="account">Deposit To</Label>
              <Select value={formData.account_id} onValueChange={(value) => setFormData({ ...formData, account_id: value })}>
                <SelectTrigger className="bg-slate-50">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(acc => (
                    <SelectItem key={acc.account_id} value={acc.account_id}>{acc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="pt-2">
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-indigo-600 text-xs p-0 h-auto"
                onClick={() => setShowTax(!showTax)}
            >
                {showTax ? '- Hide Tax Details' : '+ Add GST/Tax Details (Section 31)'}
            </Button>
          </div>

          {showTax && (
            <div className="space-y-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <Label htmlFor="hsn" className="text-xs">HSN/SAC</Label>
                        <Input
                            id="hsn"
                            size="sm"
                            className="h-8 text-sm"
                            value={formData.hsn_sac}
                            onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="pos" className="text-xs">Place of Supply</Label>
                        <Input
                            id="pos"
                            size="sm"
                            className="h-8 text-sm"
                            value={formData.place_of_supply}
                            onChange={(e) => setFormData({ ...formData, place_of_supply: e.target.value })}
                        />
                    </div>
                </div>
                <div className="flex items-center space-x-2 mb-3">
                    <Checkbox
                        id="rev-charge"
                        checked={formData.is_reverse_charge}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_reverse_charge: checked })}
                    />
                    <Label htmlFor="rev-charge" className="text-xs cursor-pointer font-medium">Is Reverse Charge applicable?</Label>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    <div className="space-y-1">
                        <Label htmlFor="rate" className="text-xs">GST %</Label>
                        <Input
                            id="rate"
                            type="number"
                            className="h-8 text-sm"
                            value={formData.gst_rate}
                            onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="cgst" className="text-xs">CGST</Label>
                        <Input
                            id="cgst"
                            type="number"
                            className="h-8 text-sm"
                            value={formData.cgst}
                            onChange={(e) => setFormData({ ...formData, cgst: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="sgst" className="text-xs">SGST</Label>
                        <Input
                            id="sgst"
                            type="number"
                            className="h-8 text-sm"
                            value={formData.sgst}
                            onChange={(e) => setFormData({ ...formData, sgst: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <Label htmlFor="igst" className="text-xs">IGST</Label>
                        <Input
                            id="igst"
                            type="number"
                            className="h-8 text-sm"
                            value={formData.igst}
                            onChange={(e) => setFormData({ ...formData, igst: e.target.value })}
                        />
                    </div>
                </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes"
              className="bg-slate-50"
              rows={3}
            />
          </div>
          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={loading} data-testid="save-income-button">
              {loading ? 'Saving...' : income ? 'Update Record' : 'Add Record'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default IncomeModal;
